import { useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { getIndividualTransactions } from "api/individual-user-management";
import {
  // IIndividualTransaction,
  IIndividualTransactionResult,
  IIndividualTransactionsPayload,
} from "api/individual-user-management/types";
import { ReactComponent as Credit } from "assets/icons/credit.svg";
import { ReactComponent as Debit } from "assets/icons/debit.svg";
import { useApp } from "contexts/app-context";
import {
  individualTableColumnCss, // individualTypeCss,
} from "features/individuals/individuals/styles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ROUTES } from "routes/constants";
import {
  formatDateTo12HourTime,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
  replaceIdParamsInRouteWithValue,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";
import Pagination, { DEFAULT_PAGINATION_DATA } from "ui/pagination";
import { Status } from "ui/status";
// import { activeTextToStatus } from "ui/status/const";
import { TStatus } from "ui/status/types";
// import { TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TTableRowData } from "ui/table/types";
import { Paragraph3 } from "ui/typography";

import { transactionsColumns } from "../../../individuals/individuals-list-table/const";
import { TransactionsSubRouteName } from "../../consts";
import { DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS } from "../consts";
import { TableHeaderCTAs } from "./TableHeaderCTAs";

const TransactionType = ({ type = "debit" }: { type: string }) => {
  return (
    <Box>
      {type === "credit" ? (
        <Box className="inline-flex items-center gap-3">
          <Credit />
          <Paragraph3>Credit</Paragraph3>
        </Box>
      ) : (
        <Box className="inline-flex items-center gap-3">
          <Debit />
          <Paragraph3>Debit</Paragraph3>
        </Box>
      )}
    </Box>
  );
};

const SelectedTransactions = () => {
  const { individualId: userId } = useParams();
  const navigate = useNavigate();
  const { handleSaveTransactionRefId } = useApp();

  const tableRef = useRef<HTMLTableElement | null>(null);
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const type = searchParams.get("type");
  const status = searchParams.get("status");
  const search = searchParams.get("search");
  const searchBy = searchParams.get("searchBy");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const filterRetryCount = searchParams.get("filterRetryCount");

  const [individualTransactionsResult, setIndividualTransactionsResult] =
    useState<IIndividualTransactionResult>({
      data: [],
      ...DEFAULT_PAGINATION_DATA,
    });
  const [isLoadingIndividualTransactions, setIsLoadingIndividualTransactions] =
    useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleGetIndividualTransactions = async (
    payload: IIndividualTransactionsPayload,
    others: {
      isRefetching: boolean;
    }
  ) => {
    const { isRefetching } = others;
    try {
      if (!isRefetching) {
        setIsLoadingIndividualTransactions(true);
      }
      const { error, message, data } = await getIndividualTransactions(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setIndividualTransactionsResult(data);
      }
      setIsLoadingIndividualTransactions(false);
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetching) {
      setIsLoadingIndividualTransactions(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    const payload: IIndividualTransactionsPayload = {
      userId,
      page: String(page || DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.page),
      limit: String(
        limit || DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.limit
      ),
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (type) {
      const transactionTypes = type.split(",");
      const filteredTypes = transactionTypes
        .filter((transactionType) => transactionType !== "all")
        .join(",");
      payload["type"] = filteredTypes;
    }
    if (status) {
      const transactionStatus = status.split(",");
      const filteredStatus = transactionStatus
        .filter((transaction) => transaction !== "all")
        .join(",");
      payload["status"] = filteredStatus;
    }

    if (search) {
      payload["search"] = search;
      payload["searchBy"] = "description,reference";
    }

    void handleGetIndividualTransactions(payload, {
      isRefetching: false,
    });
  }, [
    userId,
    page,
    limit,
    type,
    search,
    searchBy,
    status,
    startDate,
    endDate,
    filterRetryCount,
  ]);

  const { data: individualTransactions, ...paginationData } =
    individualTransactionsResult || {};

  const handleViewTransactionDetails = (transactionRowData: TTableRowData) => {
    const transactionId = transactionRowData["id"] as string;
    const transactionReference = transactionRowData["reference"] as string;
    handleSaveTransactionRefId({
      reference: transactionReference,
      id: transactionId,
    });
    const transactionDetailsRoute = replaceIdParamsInRouteWithValue({
      route: ROUTES.TRANSACTION_DETAILS_PAGE.replace(
        ":transactionRef",
        transactionReference
      ),
      idParamsName: ":transactionType",
      value: TransactionsSubRouteName.TRANSFERS,
    });
    navigate(transactionDetailsRoute);
  };

  const formattedData = individualTransactions?.map((transaction) => {
    const {
      id,
      description,
      amount,
      status,
      type,
      transactionRef,
      createdAt,
      // narration,
      newBalance,
      // walletId,
    } = transaction;

    const getTransationStatus = (status: string) => {
      let transactionStatus = status;

      if (transactionStatus === "successful") {
        transactionStatus = "success";
      } else if (transactionStatus === "failed") {
        transactionStatus = "error";
      } else if (transactionStatus === "pending") {
        transactionStatus = "warning";
      } else {
        transactionStatus = "disabled";
      }

      return transactionStatus;
    };

    return {
      "Date and Time": (
        <Box>
          <Paragraph3>
            {createdAt ? formatDateToDDMMMYYYY(createdAt) : "N/A"}
          </Paragraph3>
          <p className="uppercase text-sm text-[#807F94]">
            {formatDateTo12HourTime(createdAt)}
          </p>
        </Box>
      ),
      Type: <TransactionType type={type} />,
      Description: (
        <Box
          className="flex flex-col gap-1"
          css={individualTableColumnCss}
        >
          <Paragraph3 lineHeight="20px">
            {type === "debit" ? "Transfer to " : "From "}
          </Paragraph3>
          <Box css={individualTableColumnCss}>{description}</Box>
        </Box>
      ),
      Reference: <Box css={individualTableColumnCss}>{transactionRef}</Box>,
      Amount: (
        <Box className="font-semibold text-sm text-[#1A202C] ">
          {/* {formatAmountIntoOrdinaryDecimalAndNaira(amount)} */}
          {amount
            ? handleAmountFormat({
                value: String(amount / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </Box>
      ),
      "Current Balance": (
        <Box
          className="font-normal text-sm text-[#1A202C] "
          // css={individualTableColumnCss}
        >
          {/* {formatAmountIntoOrdinaryDecimalAndNaira(newBalance)} */}
          {newBalance
            ? handleAmountFormat({
                value: String(newBalance / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </Box>
      ),
      Status: (
        <Status
          status={getTransationStatus(status) as TStatus}
          text={status}
          className="w-fit"
        />
      ),
      id,
      reference: transactionRef,
    };
  });

  return (
    <TableWrapper className="!p-0">
      <TableHeaderCTAs
        handleGetTransactions={handleGetIndividualTransactions}
        isLoadingTransactions={isLoadingIndividualTransactions}
        isRefreshing={isRefreshing}
        setIsRefreshing={setIsRefreshing}
      />
      <Table
        columns={transactionsColumns}
        data={formattedData}
        onRowClick={handleViewTransactionDetails}
        tableRef={tableRef}
        isLoading={isLoadingIndividualTransactions && !isRefreshing}
        isRowClickable
        hasCheckbox
      />
      <Pagination
        paginationData={paginationData}
        data={individualTransactions}
        defaultPaginationParams={DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS}
        tableRef={tableRef}
        isLoadingTable={isLoadingIndividualTransactions}
      />
    </TableWrapper>
  );
};

export default SelectedTransactions;
